import { Button } from '@mantine/core';
import type { ReactNode } from 'react';

import { useWaitlistMode } from '../../lib/auth-context';
import { CTA_CLAY } from '../../lib/cta-clay';
import { JoinLink } from './JoinLink';

/**
 * SignUpButton — the canonical join-WWAAVE account CTA.
 *
 * Single source of truth for its styling (was inlined six times across the
 * three headers). Destination comes from JoinLink: `/earlyaccess` pre-launch,
 * `/sign-up` once public sign-ups open. The default label follows the
 * destination so the button never promises an account it can't create yet.
 *
 * Solid Clay, matching the homepage hero / closing / sticky artist CTAs.
 * `brand.6` IS Clay #D2602E, so an admin branding override still flows
 * through, and the label colour is left to the theme's autoContrast rather
 * than hardcoded, so a lighter brand override flips the label to ink instead
 * of going unreadable.
 *
 * NOTE ON REACH: HomeHeader, PublicHeader and AppHeader all render this, so
 * restyling it here restyles every join button on the site at once, which is
 * the point — a half-swept join button reads as a bug. All three render it
 * only in their signed-out branch (AppHeader swaps in the notification bell +
 * UserMenu once there is a user), so this button never appears on a signed-in
 * surface and cannot clash with the app's own still-gradient buttons.
 */
export function SignUpButton({
  size = 'sm',
  radius = 'md',
  fullWidth = false,
  onClick,
  children,
}: {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  radius?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  onClick?: () => void;
  children?: ReactNode;
}) {
  const { signupsOpen, loaded } = useWaitlistMode();
  const label = children ?? (loaded && signupsOpen ? 'Sign up' : 'Early access');

  return (
    <JoinLink
      style={{ display: fullWidth ? 'block' : 'inline-block' }}
      {...(onClick ? { onClick } : {})}
    >
      <Button
        component="span"
        tabIndex={-1}
        variant="filled"
        color={CTA_CLAY}
        size={size}
        radius={radius}
        fullWidth={fullWidth}
      >
        {label}
      </Button>
    </JoinLink>
  );
}
